import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, PoolPill } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Loader2, AlertTriangle, ListPlus, FileText, ArrowUp, ArrowDown, ArrowUpDown, Download, X as XIcon, DollarSign, CheckCircle2, Send, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { HorseAgeBadge } from "@/components/HorseAgeBadge";
import { formatCurrency } from "@/lib/currency";
// pdfReport is dynamically imported at call time to keep jsPDF off the initial bundle.

const POOLS = ["all", "Elite", "Good", "Average+", "Below Average", "Unsalvageable", "Unrated"];
const STATUSES = ["all", "playing", "young", "resting", "brood", "injured", "for_sale"];
const WORK_STATUSES = ["", "Playing", "In work", "Resting", "Going back to Stydrom", "Fattening"];
const LOCATIONS = ["", "Grazing", "Stables", "Other"];
const SEVERITIES = ["", "None", "Mild", "Chronic"];

export default function HorsesPage() {
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pool, setPool] = useState("all");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initialForm());

  function initialForm() {
    return {
      name: "", breed: "", sex: "", age: "", color: "", height_hands: "",
      status: "playing", notes: "",
      location: "", arrival_date: "", work_status: "",
      injured: false, injury_comments: "", severity: "", vet_comments: "",
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (pool !== "all") params.pool = pool;
      if (status !== "all") params.status = status;
      const res = await api.get("/horses/", { params });
      setHorses(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pool, status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-reload when a global Quick Edit applies changes
  useQuickEditRefresh(load, [pool, status]);

  const { isAdmin, isExecutive, isMaster, hasPerm } = useAuth();
  const canPublishPrice = isMaster || hasPerm?.("horses:publish_price");
  const canViewCost = isMaster || hasPerm?.("horses:view_cost");
  const [upkeep, setUpkeep] = useState(null); // { monthly_rate, currency }
  useEffect(() => {
    if (!canViewCost) return;
    api.get("/horse-upkeep/rate").then((r) => setUpkeep(r.data)).catch(() => setUpkeep(null));
  }, [canViewCost]);
  // Live upkeep cost = rate × days at farm.
  const costSoFar = (h) => {
    const rate = upkeep?.monthly_rate;
    if (!rate || h?.time_at_farm_days == null) return null;
    return Number(rate) * Number(h.time_at_farm_days);
  };
  // Bulk-selection state for the "Push prices" workflow. We store IDs
  // in a Set for O(1) toggle / lookup. Cleared after every push /
  // unpublish so the UI resets predictably.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [publishing, setPublishing] = useState(false);
  // When the operator ticks "Also mark as For sale" in the bulk push
  // dialog, we set this flag so the confirmation onConfirm knows to
  // include `mark_for_sale=true` in the request payload. Reset after
  // every action so the checkbox doesn't stick between separate
  // pushes.
  const [bulkMarkForSale, setBulkMarkForSale] = useState(false);
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  // `filteredForBulk` is the list of horses the "select all" checkbox
  // operates over — after the search/pool/status filters but before
  // sort (sort is display-only). Kept as a memo so the master
  // checkbox indeterminate state stays in sync as filters change.
  const doPublish = async () => {
    if (selectedIds.size === 0) return;
    setPublishing(true);
    try {
      const res = await api.post("/horses/publish-prices", {
        horse_ids: [...selectedIds],
        mark_for_sale: bulkMarkForSale,
      });
      const { count, skipped_no_price = [] } = res.data || {};
      if (count > 0) {
        toast.success(
          bulkMarkForSale
            ? `Pushed ${count} price${count === 1 ? "" : "s"} and marked as For sale.`
            : `Pushed ${count} price${count === 1 ? "" : "s"} to rankings.`
        );
      }
      if (skipped_no_price.length > 0) {
        toast.warning(
          `${skipped_no_price.length} horse${skipped_no_price.length === 1 ? " was" : "s were"} skipped: no internal price set yet.`
        );
      }
      // Reflect the new listed_price + status locally without a full
      // reload, and broadcast to any RankingsPage instance in this
      // browser so it refreshes instantly.
      const publishedMap = new Map((res.data?.published || []).map((p) => [p.id, p]));
      setHorses((prev) => prev.map((h) => {
        const p = publishedMap.get(h.id);
        return p ? { ...h, listed_price: p.listed_price, status: p.status } : h;
      }));
      window.dispatchEvent(new CustomEvent("horses:prices-published"));
      clearSelection();
      setBulkMarkForSale(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to push prices");
      throw e;
    } finally {
      setPublishing(false);
    }
  };
  const doUnpublish = async () => {
    if (selectedIds.size === 0) return;
    setPublishing(true);
    try {
      const res = await api.post("/horses/unpublish-prices", { horse_ids: [...selectedIds] });
      const count = res.data?.count ?? 0;
      if (count > 0) toast.success(`Unpublished ${count} price${count === 1 ? "" : "s"}.`);
      const unpublishedSet = new Set(res.data?.unpublished || []);
      setHorses((prev) => prev.map((h) => (
        unpublishedSet.has(h.id) ? { ...h, listed_price: null } : h
      )));
      window.dispatchEvent(new CustomEvent("horses:prices-published"));
      clearSelection();
    } catch (e) {
      toast.error("Failed to unpublish prices");
      throw e;
    } finally {
      setPublishing(false);
    }
  };

  // Pool distribution counts (live from current load — not server-filtered so it
  // always reflects the full inventory, even when a pool filter is applied).
  const poolCounts = useMemo(() => {
    const counts = { Elite: 0, Good: 0, "Average+": 0, "Below Average": 0, Unsalvageable: 0, Unrated: 0 };
    for (const h of horses) {
      const p = h?.ranking?.pool || "Unrated";
      counts[p] = (counts[p] || 0) + 1;
    }
    return counts;
  }, [horses]);

  const [sortKey, setSortKey] = useState(null);   // null = use default order
  const [sortDir, setSortDir] = useState("asc");  // "asc" | "desc"

  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key); setSortDir("asc"); return;
    }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    // 3rd click resets to default order
    setSortKey(null); setSortDir("asc");
  };

  /** Compute the value used to sort a row by a given column key. */
  const sortValue = (h, key) => {
    switch (key) {
      case "name":        return (h.name || "").toLowerCase();
      case "age":         return h.age ?? Number.POSITIVE_INFINITY;
      case "location":    return (h.location || "").toLowerCase();
      case "time_at_farm":{
        if (!h.arrival_date) return Number.POSITIVE_INFINITY;
        const d = new Date(h.arrival_date);
        return isNaN(d) ? Number.POSITIVE_INFINITY : d.getTime();
      }
      case "class":       return (h.computed_class || "").toLowerCase();
      case "work_status": return (h.work_status || "").toLowerCase();
      case "injured":     return h.injured ? 1 : 0;
      case "pool":        return (h.ranking?.pool || "").toLowerCase();
      case "score":       return h.ranking?.score ?? -1;
      case "purchase_price": return h.purchase_price ?? -1;
      case "purchased_from": return (h.purchased_from || "").toLowerCase();
      default:            return 0;
    }
  };

  const filtered = (() => {
    const list = horses.filter((h) => !search || h.name?.toLowerCase().includes(search.toLowerCase()));
    if (!sortKey) return list;
    const sorted = [...list].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  })();

  const createHorse = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseFloat(form.age) : null,
        height_hands: form.height_hands ? parseFloat(form.height_hands) : null,
        arrival_date: form.arrival_date || null,
        location: form.location || null,
        work_status: form.work_status || null,
        severity: form.severity || null,
        injury_comments: form.injury_comments || null,
        vet_comments: form.vet_comments || null,
      };
      await api.post("/horses/", payload);
      toast.success("Horse added. class computed from age");
      setOpen(false);
      setForm(initialForm());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const exportPdf = async () => {
    try {
      // Use the visible (filtered) list so the report matches what the user sees.
      const visibleHorses = filtered;
      const rows = visibleHorses.map((h) => ({
        name: h.name,
        age: h.age != null ? `${h.age}y` : "",
        sex: h.sex || "",
        breed: h.breed || "",
        location: h.location || "",
        work_status: h.work_status || "",
        pool: h?.ranking?.pool || "Unrated",
        score: h?.ranking?.score != null ? Number(h.ranking.score).toFixed(1) : "",
        injury: h.injured ? (h.severity ? `Injured (${h.severity})` : "Injured") : "",
      }));
      const { downloadReport } = await import("@/lib/pdfReport");
      await downloadReport({
        eyebrow: "OPERATIONS",
        title: "Horses. Live Inventory",
        period: `Filtered view: ${pool === "all" ? "all pools" : pool}${status !== "all" ? `, ${status}` : ""}${search ? `, "${search}"` : ""}`,
        filename: "horses-live-inventory",
        summary: [
          { label: "Total", value: visibleHorses.length },
          { label: "Injured", value: visibleHorses.filter((h) => h.injured).length },
        ],
        columns: [
          { header: "Name", dataKey: "name" },
          { header: "Age", dataKey: "age", mono: true, halign: "right" },
          { header: "Sex", dataKey: "sex" },
          { header: "Breed", dataKey: "breed" },
          { header: "Location", dataKey: "location" },
          { header: "Work status", dataKey: "work_status" },
          { header: "Pool", dataKey: "pool" },
          { header: "Score", dataKey: "score", mono: true, halign: "right" },
          { header: "Injury", dataKey: "injury" },
        ],
        rows,
      });
    } catch (e) {
      toast.error("Could not generate PDF");
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Horses. Live Inventory"
        subtitle={`${horses.length} horse${horses.length===1?"":"s"}. full Live Horse Inventory schema with auto-computed class`}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={exportPdf}
              disabled={loading || horses.length === 0}
              data-testid="horses-download-pdf-button"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            {(isAdmin || isMaster) && (
              <Button
                variant="outline"
                onClick={() => window.location.assign("/horses/import-ratings")}
                data-testid="horses-import-ratings-button"
                title="Import legacy rider notes from a PDF, Excel or CSV"
              >
                <FileText className="h-4 w-4 mr-2" /> Import legacy ratings
              </Button>
            )}
            <BulkAddHorsesDialog onSaved={load} />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="horses-add-button"><Plus className="h-4 w-4 mr-2" /> Add horse</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>Add a new horse. Live Inventory</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *" testId="new-horse-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <Field label="Age (years)" testId="new-horse-age" type="number" step="0.25" value={form.age} onChange={(v) => setForm({ ...form, age: v })} placeholder="e.g. 8 or 1.5" />
                <SelectField label="Sex" value={form.sex} onChange={(v) => setForm({ ...form, sex: v })} options={["", "gelding", "mare", "stallion", "filly", "colt"]} testId="new-horse-sex" />
                <Field label="Breed" testId="new-horse-breed" value={form.breed} onChange={(v) => setForm({ ...form, breed: v })} />
                <Field label="Colour" testId="new-horse-color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} />
                <Field label="Height (hands)" testId="new-horse-height" type="number" step="0.25" value={form.height_hands} onChange={(v) => setForm({ ...form, height_hands: v })} />
                <SelectField label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} options={LOCATIONS} allowCustom />
                <Field label="Arrival date" type="date" value={form.arrival_date} onChange={(v) => setForm({ ...form, arrival_date: v })} testId="new-horse-arrival-date" />
                <SelectField label="Work status" value={form.work_status} onChange={(v) => setForm({ ...form, work_status: v })} options={WORK_STATUSES} testId="new-horse-work-status" />
                <SelectField label="Status (system)" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES.slice(1)} testId="new-horse-status" />
                <div className="col-span-2 border-t pt-3 mt-1">
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" id="new-horse-injured" checked={form.injured} onChange={(e) => setForm({ ...form, injured: e.target.checked })} data-testid="new-horse-injured" className="h-4 w-4" />
                    <Label htmlFor="new-horse-injured" className="text-sm font-medium">Currently injured</Label>
                  </div>
                  {form.injured && (
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Injury comments" value={form.injury_comments} onChange={(v) => setForm({ ...form, injury_comments: v })} colSpan={2} />
                      <SelectField label="Severity" value={form.severity} onChange={(v) => setForm({ ...form, severity: v })} options={SEVERITIES} />
                    </div>
                  )}
                  <div className="mt-3">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Vet comments</Label>
                    <Input value={form.vet_comments} onChange={(e) => setForm({ ...form, vet_comments: e.target.value })} />
                  </div>
                </div>
                <Field label="Notes" testId="new-horse-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} colSpan={2} />
                {form.age && (
                  <div className="col-span-2 rounded-md border bg-[hsl(var(--ap-paper))] p-3 text-xs">
                    <span className="text-muted-foreground">Computed Class (auto):</span>{" "}
                    <span className="font-medium">{previewClass(form.age)}</span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={createHorse} disabled={creating} data-testid="new-horse-submit">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add horse"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <Card className="rounded-xl overflow-hidden">
        {/* Pool distribution badges. clickable to filter the table */}
        <div className="px-4 pt-4 pb-2 flex flex-wrap gap-2" data-testid="horses-pool-distribution">
          {Object.entries(poolCounts).map(([name, n]) => {
            if (n === 0) return null;
            const active = pool === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setPool(active ? "all" : name)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition ${
                  active
                    ? "bg-primary/10 border-primary text-primary"
                    : "bg-muted/30 border-border text-muted-foreground hover:bg-muted/60"
                }`}
                data-testid={`horses-pool-pill-${name}`}
              >
                <span className="font-medium">{name}</span>
                <span className="rounded-full bg-background/60 px-1.5 text-[10px] font-semibold">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="sticky top-0 z-10 p-4 border-b bg-[hsl(var(--ap-paper))] flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search horse name…" className="pl-9" data-testid="horses-filter-search-input" />
          </div>
          <Select value={pool} onValueChange={setPool}>
            <SelectTrigger className="w-[150px]" data-testid="horses-filter-pool-select"><SelectValue placeholder="Pool" /></SelectTrigger>
            <SelectContent>{POOLS.map((p) => <SelectItem key={p} value={p}>{p === "all" ? "All pools" : p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[150px]" data-testid="horses-filter-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="ghost" onClick={() => { setSearch(""); setPool("all"); setStatus("all"); }} data-testid="horses-filter-reset-button">Reset</Button>
        </div>
        {/* Bulk price-publish toolbar — appears in-place above the table
            when the caller has `horses:publish_price` and has at least
            one horse selected. Push copies each selected horse's
            internal purchase_price into listed_price, which is what
            everyone (including rankings-only viewers) sees. Unpublish
            wipes the listed price back to null. There is no changelog. */}
        {canPublishPrice && selectedIds.size > 0 && (
          <div
            className="mx-4 mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-[hsl(var(--ap-brass)/0.35)] bg-[hsl(var(--ap-brass)/0.08)] px-3 py-2"
            data-testid="horses-bulk-price-toolbar"
          >
            <DollarSign className="h-4 w-4 text-[hsl(var(--ap-saddle))]" strokeWidth={2} />
            <div className="text-sm flex-1 min-w-0">
              <span className="font-medium" data-testid="horses-bulk-selection-count">
                {selectedIds.size} horse{selectedIds.size === 1 ? "" : "s"} selected
              </span>
              <span className="text-muted-foreground ml-2">
                — Push copies the internal price to the public rankings.
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={publishing}
              data-testid="horses-bulk-clear-button"
            >
              Clear
            </Button>
            <ConfirmDialog
              title={`Push ${selectedIds.size} price${selectedIds.size === 1 ? "" : "s"} to the leaderboard?`}
              description={
                <div className="space-y-3">
                  <p>The current internal price of each selected horse will be copied to the public listed price and become visible to everyone on the Rankings page (including staff without horse permissions).</p>
                  <p className="text-xs">Any horse without an internal price will be skipped. Re-pushing after a price change overwrites — there is no history of the previous number.</p>
                  {/* Bundled for-sale toggle. Keeping this decision
                      right next to the price-push button because
                      operationally they're a single act — you push a
                      price BECAUSE you're marketing the horse.
                      Optional so operators can also re-push to just
                      correct a stale figure without changing sale
                      status. */}
                  <label className="flex items-start gap-2 rounded-md border border-border/80 bg-muted/30 p-2.5 cursor-pointer hover:bg-muted/50">
                    <input
                      type="checkbox"
                      checked={bulkMarkForSale}
                      onChange={(e) => setBulkMarkForSale(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-input"
                      data-testid="bulk-push-for-sale-checkbox"
                    />
                    <span className="text-xs">
                      <span className="font-medium text-foreground">Also mark all as "For sale"</span>
                      <span className="block text-muted-foreground mt-0.5">
                        Sets the horse status so buyers browsing the rankings see the "For sale" badge next to the price. Leave unchecked if you're just correcting a price on horses that are already listed (or not for sale).
                      </span>
                    </span>
                  </label>
                </div>
              }
              confirmLabel={bulkMarkForSale ? "Push & mark for sale" : "Yes, push prices"}
              destructive={false}
              onConfirm={doPublish}
              onOpenChange={(v) => { if (!v) setBulkMarkForSale(false); }}
              testId="horses-bulk-push"
            >
              <Button
                variant="default"
                size="sm"
                disabled={publishing}
                data-testid="horses-bulk-push-button"
              >
                {publishing
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Push prices
              </Button>
            </ConfirmDialog>
            <ConfirmDialog
              title={`Unpublish ${selectedIds.size} price${selectedIds.size === 1 ? "" : "s"}?`}
              description={
                <>
                  <p>The listed price on each selected horse will be removed from the leaderboard. Staff will see the price disappear on their next refresh (or within a minute automatically).</p>
                  <p className="mt-2 text-xs">Internal purchase prices are untouched. You can re-push at any time.</p>
                </>
              }
              confirmLabel="Yes, unpublish"
              destructive={false}
              onConfirm={doUnpublish}
              testId="horses-bulk-unpublish"
            >
              <Button
                variant="outline"
                size="sm"
                disabled={publishing}
                data-testid="horses-bulk-unpublish-button"
              >
                <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Unpublish
              </Button>
            </ConfirmDialog>
          </div>
        )}
        {/* Desktop / tablet table */}
        <div className="hidden md:block overflow-auto" data-testid="horses-table">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ap-paper))]">
              <tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
                {canPublishPrice && (
                  <th className="w-10 px-3 py-3">
                    {/* Master select-all checkbox — ticks all currently-filtered
                        horses that have an internal price (only those are eligible
                        to be pushed). Indeterminate state when a subset is
                        selected so it doesn't visually lie. */}
                    <Checkbox
                      checked={
                        filtered.length > 0 &&
                        filtered.every((h) => selectedIds.has(h.id))
                          ? true
                          : filtered.some((h) => selectedIds.has(h.id))
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={(v) => {
                        if (v === true) {
                          setSelectedIds(new Set(filtered.map((h) => h.id)));
                        } else {
                          clearSelection();
                        }
                      }}
                      aria-label="Select all filtered horses"
                      data-testid="horses-select-all-checkbox"
                    />
                  </th>
                )}
                <SortHeader label="Name"         k="name"         sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Age"          k="age"          sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Location"     k="location"     sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Time at farm" k="time_at_farm" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Class"        k="class"        sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Work status"  k="work_status"  sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Injured?"     k="injured"      sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Pool"         k="pool"         sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                {isAdmin && <SortHeader label="Price"  k="purchase_price"  sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />}
                {isAdmin && <SortHeader label="Source" k="purchased_from"  sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />}
                {canViewCost && <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-muted-foreground font-medium" title="Live upkeep cost = rate × days at farm">Cost so far</th>}
                <SortHeader label="Score"        k="score"        sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={(canPublishPrice ? 1 : 0) + (isAdmin ? 11 : 9) + (canViewCost ? 1 : 0)} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={(canPublishPrice ? 1 : 0) + (isAdmin ? 11 : 9) + (canViewCost ? 1 : 0)} className="text-center py-12 text-muted-foreground">No horses match your filters.</td></tr>
              ) : filtered.map((h) => (
                <tr key={h.id} className={`border-b hover:bg-accent/60 ${selectedIds.has(h.id) ? "bg-[hsl(var(--ap-brass)/0.06)]" : ""}`} data-testid={`horse-row-${h.id}`}>
                  {canPublishPrice && (
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selectedIds.has(h.id)}
                        onCheckedChange={() => toggleSelect(h.id)}
                        aria-label={`Select ${h.name}`}
                        data-testid={`horse-select-${h.id}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Link to={`/horses/${h.id}`} className="flex items-center gap-2.5 font-medium hover:underline" data-testid={`horse-name-${h.id}`}>
                      {h.photo_thumb_url ? (
                        <img
                          src={h.photo_thumb_url}
                          alt=""
                          className="h-8 w-8 rounded-full object-cover border border-border"
                          loading="lazy"
                          data-testid={`horse-thumb-${h.id}`}
                        />
                      ) : (
                        <span className="h-8 w-8 rounded-full bg-muted/50 inline-flex items-center justify-center text-[10px] text-muted-foreground border border-border" aria-hidden>
                          {(h.name || "?").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <span>{h.name}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums" data-testid={`horse-age-${h.id}`}>
                    <HorseAgeBadge age={h.age} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{h.location || ""}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">{h.time_at_farm_days != null ? `${h.time_at_farm_days}d` : "∞"}</td>
                  <td className="px-4 py-3 text-xs">{h.computed_class ? <ClassPill cls={h.computed_class} /> : ""}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{h.work_status || ""}</td>
                  <td className="px-4 py-3">{h.injured ? <span className="inline-flex items-center gap-1 text-[hsl(var(--destructive))] text-xs"><AlertTriangle className="h-3 w-3" />{h.severity || "yes"}</span> : <span className="text-muted-foreground text-xs">No</span>}</td>
                  <td className="px-4 py-3"><PoolPill pool={h.ranking?.pool} stars={h.ranking?.stars} /></td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right text-xs" data-testid={`horse-price-${h.id}`}>
                      <PriceStatusCell
                        purchasePrice={h.purchase_price}
                        listedPrice={h.listed_price}
                      />
                    </td>
                  )}
                  {isAdmin && (
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[180px] truncate" title={h.purchased_from || ""} data-testid={`horse-source-${h.id}`}>
                      {h.purchased_from || ""}
                    </td>
                  )}
                  {canViewCost && (
                    <td className="px-4 py-3 text-right tabular-nums text-xs" data-testid={`horse-cost-so-far-${h.id}`}>
                      {costSoFar(h) != null
                        ? formatCurrency(Math.round(costSoFar(h)), upkeep?.currency || "ZAR")
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{h.ranking?.score?.toFixed?.(1) ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y" data-testid="horses-cards">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">No horses match your filters.</div>
          ) : filtered.map((h) => (
            <Link
              key={h.id}
              to={`/horses/${h.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-accent/60 active:bg-accent/80 transition-colors"
              data-testid={`horse-card-${h.id}`}
            >
              {h.photo_thumb_url ? (
                <img
                  src={h.photo_thumb_url}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover border border-border shrink-0"
                  loading="lazy"
                />
              ) : (
                <span className="h-12 w-12 rounded-full bg-muted/50 inline-flex items-center justify-center text-sm text-muted-foreground border border-border shrink-0" aria-hidden>
                  {(h.name || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate" data-testid={`horse-card-name-${h.id}`}>{h.name}</span>
                  <PoolPill pool={h.ranking?.pool} stars={h.ranking?.stars} />
                  {h.injured && (
                    <span className="inline-flex items-center gap-1 text-[hsl(var(--destructive))] text-[10px] uppercase tracking-wider">
                      <AlertTriangle className="h-3 w-3" />{h.severity || "injured"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <HorseAgeBadge age={h.age} />
                  {h.location && <><span>·</span><span>{h.location}</span></>}
                  {h.work_status && <><span>·</span><span className="truncate">{h.work_status}</span></>}
                </div>
                {h.computed_class && (
                  <div className="mt-1.5">
                    <ClassPill cls={h.computed_class} />
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-base tabular-nums font-medium">
                  {h.ranking?.score?.toFixed?.(1) ?? ""}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">score</div>
              </div>
            </Link>
          ))}
        </div>
      </Card>

      {/* Conversational quick-edit (LLM-powered, applies immediately) */}
    </div>
  );
}

function formatAge(h) {
  if (h.age == null) return "";
  const a = parseFloat(h.age);
  if (a < 2) return `${Math.round(a * 12)} mo`;
  return `${a}y`;
}

function previewClass(age) {
  if (!age) return "";
  const m = parseFloat(age) * 12;
  if (m < 36) return "Blocked (Age ≤ 3) - Eligible for Play";
  if (m < 40) return "Blocked (Age ≤ 3) - Eligible for Class 4";
  if (m < 48) return "Class 4 - 2-4 Goal Tournaments";
  if (m < 60) return "Class 3 - August Tournaments and Formative chukkas";
  return "Playing";
}

function ClassPill({ cls }) {
  const isBlocked = cls?.startsWith("Blocked");
  const isClass4 = cls?.startsWith("Class 4");
  const isClass3 = cls?.startsWith("Class 3");
  const isPlaying = cls === "Playing";
  const color = isPlaying ? "bg-[hsl(var(--ap-success)/0.10)] text-[hsl(var(--ap-success))] border-[hsl(var(--ap-success)/0.25)]"
    : isClass3 ? "bg-[hsl(var(--ap-info)/0.10)] text-[hsl(var(--ap-info))] border-[hsl(var(--ap-info)/0.25)]"
    : isClass4 ? "bg-[hsl(var(--ap-brass)/0.12)] text-[hsl(var(--ap-saddle))] border-[hsl(var(--ap-brass)/0.30)]"
    : isBlocked ? "bg-[hsl(var(--ap-warning)/0.10)] text-[hsl(var(--ap-warning))] border-[hsl(var(--ap-warning)/0.30)]"
    : "bg-muted text-muted-foreground border-border";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${color}`} title={cls}>{shortClass(cls)}</span>;
}

function shortClass(c) {
  if (!c) return "";
  if (c.startsWith("Blocked")) return c.includes("Class 4") ? "Blocked → C4" : "Blocked → Play";
  if (c.startsWith("Class 4")) return "Class 4";
  if (c.startsWith("Class 3")) return "Class 3";
  return c;
}

function Field({ label, value, onChange, type = "text", step, colSpan = 1, placeholder = "", testId }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input type={type} step={step} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, allowCustom = false, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {allowCustom ? (
        <Input value={value} onChange={(e) => onChange(e.target.value)} list={`${label}-options`} placeholder="Choose or type…" data-testid={testId} />
      ) : (
        <Select value={value || "__empty"} onValueChange={(v) => onChange(v === "__empty" ? "" : v)}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder="" /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o || "__empty"} value={o || "__empty"}>{o || ""}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {allowCustom && <datalist id={`${label}-options`}>{options.map((o) => o ? <option key={o} value={o} /> : null)}</datalist>}
    </div>
  );
}


function BulkAddHorsesDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setText(""); setRows(null); };

  const parse = async () => {
    if (!text.trim()) { toast.error("Paste at least one horse name"); return; }
    setParsing(true);
    try {
      const res = await api.post("/horses/parse-bulk-text", { text });
      // Give each parsed row a stable client-side id so React key stays
      // consistent even when a user deletes a row from the middle. Without
      // this the inputs get reused with stale values from the row that
      // took their position (classic index-as-key bug).
      const withIds = (res.data.rows || []).map((r, idx) => ({
        __id: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `row-${Date.now()}-${idx}`),
        ...r,
      }));
      setRows(withIds);
      if (!res.data.rows.length) toast.error("No valid rows found. each line needs at least a name");
    } catch (e) { toast.error("Parse failed"); }
    finally { setParsing(false); }
  };

  const updateRow = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    setRows(next);
  };

  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const commit = async () => {
    if (!rows || rows.length === 0) { toast.error("Nothing to add"); return; }
    setSaving(true);
    try {
      const horses = rows.map((r) => ({
        ...r,
        age: r.age === "" || r.age == null ? null : parseFloat(r.age),
      }));
      const res = await api.post("/horses/bulk", { horses });
      const c = res.data.created_count;
      const e = res.data.error_count;
      if (c > 0) toast.success(`Added ${c} horse${c === 1 ? "" : "s"}${e ? ` (${e} skipped)` : ""}`);
      if (e > 0) {
        const first = res.data.errors[0];
        toast.error(`${e} skipped: ${first.name} → ${first.error}`);
      }
      setOpen(false);
      reset();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="horses-bulk-add-button"><ListPlus className="h-4 w-4 mr-2" /> Bulk add</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto">
        <DialogHeader><DialogTitle>Bulk add horses</DialogTitle></DialogHeader>
        {!rows ? (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Paste one horse per line. Columns separated by <code className="bg-muted px-1 rounded">,</code> or tab (e.g. straight from Excel).
              <br/>Order: <span className="font-mono text-xs">name, age, sex, breed, color, location, work_status, notes</span>. only <span className="font-mono">name</span> is required.
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"Bella, 8, mare, TB, bay, Grazing, Playing\nCaptain, 11, gelding\nMamaba, 3, filly\nSpark"}
              rows={10}
              className="font-mono text-sm"
              data-testid="bulk-add-textarea"
            />
            <DialogFooter>
              <Button onClick={parse} disabled={parsing} data-testid="bulk-parse-button">
                {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-2" /> Preview rows</>}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">{rows.length}</span> rows parsed. Review & edit before saving.</p>
              <Button variant="ghost" size="sm" onClick={() => setRows(null)} data-testid="bulk-back-button">← Back to paste</Button>
            </div>
            <div className="overflow-auto rounded-md border max-h-[480px]" data-testid="bulk-preview-table">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--ap-paper))] sticky top-0">
                  <tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
                    <th className="text-left px-2 py-2">Name *</th>
                    <th className="text-left px-2 py-2">Age</th>
                    <th className="text-left px-2 py-2">Sex</th>
                    <th className="text-left px-2 py-2">Breed</th>
                    <th className="text-left px-2 py-2">Colour</th>
                    <th className="text-left px-2 py-2">Location</th>
                    <th className="text-left px-2 py-2">Work status</th>
                    <th className="text-left px-2 py-2">Class (auto)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.__id} className="border-b">
                      <td className="px-1 py-1"><Input value={r.name || ""} onChange={(e) => updateRow(i, "name", e.target.value)} className="h-8 text-xs" /></td>
                      <td className="px-1 py-1"><Input value={r.age ?? ""} onChange={(e) => updateRow(i, "age", e.target.value)} className="h-8 text-xs w-16" /></td>
                      <td className="px-1 py-1"><Input value={r.sex || ""} onChange={(e) => updateRow(i, "sex", e.target.value)} className="h-8 text-xs w-24" /></td>
                      <td className="px-1 py-1"><Input value={r.breed || ""} onChange={(e) => updateRow(i, "breed", e.target.value)} className="h-8 text-xs w-24" /></td>
                      <td className="px-1 py-1"><Input value={r.color || ""} onChange={(e) => updateRow(i, "color", e.target.value)} className="h-8 text-xs w-24" /></td>
                      <td className="px-1 py-1"><Input value={r.location || ""} onChange={(e) => updateRow(i, "location", e.target.value)} className="h-8 text-xs w-24" /></td>
                      <td className="px-1 py-1"><Input value={r.work_status || ""} onChange={(e) => updateRow(i, "work_status", e.target.value)} className="h-8 text-xs w-24" /></td>
                      <td className="px-1 py-1 text-xs text-muted-foreground">{r.computed_class ? <ClassPill cls={r.computed_class} /> : ""}</td>
                      <td className="px-1 py-1"><Button variant="ghost" size="sm" onClick={() => removeRow(i)} className="h-7 px-2" aria-label="Remove row"><XIcon className="h-3.5 w-3.5" strokeWidth={1.5} /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button onClick={commit} disabled={saving || rows.length === 0} data-testid="bulk-commit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Add {rows.length} horse{rows.length === 1 ? "" : "s"}</>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/**
 * Clickable sortable column header.
 * 3-state cycle: unsorted → asc → desc → unsorted.
 */
function SortHeader({ label, k, sortKey, sortDir, onClick, align = "left" }) {
  const isActive = sortKey === k;
  const Icon = !isActive ? ArrowUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onClick(k)}
        data-testid={`horses-sort-${k}`}
        className={`group inline-flex items-center gap-1.5 select-none uppercase tracking-[0.14em] transition-colors ${
          isActive ? "text-[hsl(var(--ap-forest))]" : "hover:text-foreground"
        }`}
        aria-sort={isActive ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        <Icon
          className={`h-3 w-3 transition-opacity ${
            isActive ? "opacity-100" : "opacity-40 group-hover:opacity-80"
          }`}
        />
      </button>
    </th>
  );
}

/**
 * Compact "price + published status" cell for the horses inventory
 * table. Shows the internal purchase price on top and a small pill
 * below indicating whether that price has been pushed to the public
 * leaderboard.
 *
 * States:
 *   - No purchase_price → "—" muted, no pill.
 *   - Has price, not published → USD price + "Not published" grey pill.
 *   - Has price, listed_price equals price → USD price + "Live" green pill.
 *   - Has price, listed_price differs → shows both with an "Update
 *     ready" amber pill to remind the operator to re-push.
 */
function PriceStatusCell({ purchasePrice, listedPrice }) {
  const hasPurchase = Number.isFinite(purchasePrice);
  const hasListed = Number.isFinite(listedPrice);
  if (!hasPurchase) {
    return <span className="text-muted-foreground">—</span>;
  }
  const priceStr = `$ ${purchasePrice.toLocaleString()}`;
  let pill = null;
  if (!hasListed) {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 text-muted-foreground px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider"
        title="This price is only visible internally. Push it from the toolbar above."
      >
        Not pushed
      </span>
    );
  } else if (Math.abs(listedPrice - purchasePrice) < 0.5) {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-forest)/0.4)] bg-[hsl(var(--ap-forest)/0.10)] text-[hsl(var(--ap-forest))] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider"
        title="The internal price and the price on the leaderboard match."
      >
        <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.4} /> Live
      </span>
    );
  } else {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-brass)/0.5)] bg-[hsl(var(--ap-brass)/0.12)] text-[hsl(var(--ap-saddle))] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider"
        title={`Rankings still shows $ ${listedPrice.toLocaleString()}. Push again to update.`}
      >
        Update ready
      </span>
    );
  }
  return (
    <div className="inline-flex flex-col items-end gap-0.5">
      <span className="font-mono tabular-nums">{priceStr}</span>
      {pill}
    </div>
  );
}

